import { MarketDataType, type Ticker, type TickerList } from '@/types/generated/common/types'
import type { MarketDataStream, MessageSubject } from '@/types/generated/public_api'

/**
 * Ticker channels outlive their last subscriber by this long before the wire unsubscribe goes
 * out, so a page switch (old page unmounts, new page mounts the same symbols) keeps the stream
 * instead of dropping it and waiting a round trip for it to come back.
 *
 * Only ticker channels linger: the cache below folds every ticker message into a symbol's
 * complete state, which is a valid snapshot to hand a new subscriber. Delta channels (order
 * book, incremental ticker, trades) would miss updates while nobody listens and need a fresh
 * server snapshot anyway, so reusing them saves nothing.
 */
export const TICKER_UNSUBSCRIBE_GRACE_MS = 5_000

/** Bounds how many unused ticker channels a fast-scrolled watchlist can leave on the wire. */
export const MAX_LINGERING_TICKER_CHANNELS = 200

type Channel = Partial<MessageSubject>

export type GetCachedTicker = (exchange: unknown, symbol: string) => Ticker | undefined

export function isTickerChannel(
  channel: Channel
): channel is Channel & { marketDataStream: MarketDataStream } {
  return channel.marketDataStream?.dataType === MarketDataType.MARKET_DATA_TYPE_TICKER
}

export function shouldLingerTickerChannel(
  channel: Channel,
  socketOpen: boolean,
  authenticated: boolean
): boolean {
  return socketOpen && authenticated && isTickerChannel(channel)
}

function tickerKey(exchange: unknown, symbol: string): string {
  return `${exchange}:${symbol}`
}

/**
 * Tracks channels (by subscription key) that lost their last subscriber but stay on the wire
 * for the grace period. Keys released together share one timer, so a page teardown's worth of
 * unsubscribes expires into a single `onExpire` call (one wire message).
 */
export function createLingeringChannels(
  onExpire: (keys: string[]) => void,
  graceMs = TICKER_UNSUBSCRIBE_GRACE_MS,
  maxLingering = MAX_LINGERING_TICKER_CHANNELS
) {
  const timers = new Map<string, ReturnType<typeof setTimeout>>()

  return {
    has: (key: string) => timers.has(key),
    /** Stops a key lingering; true if it was, i.e. the channel is still on the wire. */
    take: (key: string) => timers.delete(key),
    /** Starts `keys` lingering; returns the oldest keys over the cap, to release right away. */
    hold(keys: string[]): string[] {
      if (keys.length === 0) return []
      const timer = setTimeout(() => {
        const due = Array.from(timers).flatMap(([key, batch]) => (batch === timer ? [key] : []))
        due.forEach((key) => timers.delete(key))
        if (due.length > 0) onExpire(due)
      }, graceMs)
      keys.forEach((key) => timers.set(key, timer))
      // Map order is insertion order, so the oldest come first.
      const evicted = Array.from(timers.keys()).slice(0, Math.max(0, timers.size - maxLingering))
      evicted.forEach((key) => timers.delete(key))
      return evicted
    },
    dispose() {
      new Set(timers.values()).forEach(clearTimeout)
      timers.clear()
    },
  }
}

/**
 * Ticker frames can be partial (Hyperliquid's BBO frames omit open interest and funding), so a
 * field a frame leaves out keeps its previous value, as in the backend's snapshot overlay.
 */
function overlayTicker(previous: Ticker | undefined, next: Ticker): Ticker {
  if (!previous) return next
  const present = Object.entries(next).filter(([, value]) => value !== undefined)
  return { ...previous, ...(Object.fromEntries(present) as Partial<Ticker>) }
}

/**
 * The latest state per exchange/symbol seen on one socket. An entry is only trustworthy while
 * its ticker channel has stayed subscribed since the entry was recorded, so the provider calls
 * `forget` whenever a ticker channel is newly subscribed or finally unsubscribed, and `clear`
 * when the socket closes.
 */
export function createTickerChannelCache() {
  const tickers = new Map<string, Ticker>()

  return {
    record(list: TickerList | undefined) {
      list?.tickers.forEach((ticker) => {
        const key = tickerKey(ticker.exchange, ticker.symbol)
        tickers.set(key, overlayTicker(tickers.get(key), ticker))
      })
    },
    get(exchange: unknown, symbol: string): Ticker | undefined {
      return tickers.get(tickerKey(exchange, symbol))
    },
    /** Drops the cached ticker for a ticker channel; other channels are ignored. */
    forget(channel: Channel) {
      if (!isTickerChannel(channel)) return
      const { exchange, symbol } = channel.marketDataStream
      tickers.delete(tickerKey(exchange, symbol))
    },
    clear() {
      tickers.clear()
    },
  }
}

interface TickerPayload {
  exchange: unknown
  symbol: string
  dataType: MarketDataType
}

/**
 * The cached tickers for a stream hook's ticker payloads, shaped like a live message, so a
 * widget that mounts onto an already-live ticker channel renders at once instead of waiting
 * for the symbol's next tick.
 */
export function replayCachedTickers(
  getCachedTicker: GetCachedTicker,
  payloads: readonly TickerPayload[]
): TickerList | undefined {
  const tickers = payloads.flatMap((payload) => {
    if (payload.dataType !== MarketDataType.MARKET_DATA_TYPE_TICKER) return []
    const ticker = getCachedTicker(payload.exchange, payload.symbol)
    return ticker ? [ticker] : []
  })
  return tickers.length > 0 ? { tickers } : undefined
}
